import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Header search → the exact record. A result links to its page with
 * ?focus=<record id>; the page reads it once (useSearchFocusParam, which
 * removes it from the URL so Back and a refresh don't repeat it) and hands it
 * to its section, which opens the record's list / tab, selects it
 * (useRevealSearchTarget), and the record's row — marked
 * data-search-id="<id>" — is scrolled into view and ringed for a moment.
 */

export interface SearchFocus {
  id: string;
  /** Changes on every request, so picking the same record twice still works. */
  key: number;
}

/** The ?focus=<id> request on this page, read once and then removed from the URL. */
export function useSearchFocusParam(param = "focus"): SearchFocus | undefined {
  const [searchParams, setSearchParams] = useSearchParams();
  const [focus, setFocus] = useState<SearchFocus | undefined>(undefined);
  const requested = searchParams.get(param);
  useEffect(() => {
    if (!requested) return;
    setFocus((current) => ({ id: requested, key: (current?.key ?? 0) + 1 }));
    setSearchParams((params) => {
      params.delete(param);
      return params;
    }, { replace: true });
  }, [requested, param, setSearchParams]);
  return focus;
}

const REVEAL_FOR_MS = 3000;

/** Scrolls the row marked data-search-id="<id>" into view and rings it (it may take a few renders to appear). */
export function revealSearchRow(id: string): () => void {
  let frame = 0;
  const startedAt = Date.now();
  const escaped = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(id) : id.replace(/["\\]/g, "\\$&");
  const selector = `[data-search-id="${escaped}"]`;
  const attempt = () => {
    const row = document.querySelector<HTMLElement>(selector);
    if (row) {
      row.scrollIntoView?.({ block: "center", behavior: "smooth" });
      row.classList.remove("msq-search-flash");
      void row.offsetWidth; // restart the animation for a repeat pick
      row.classList.add("msq-search-flash");
      window.setTimeout(() => row.classList.remove("msq-search-flash"), 2600);
      return;
    }
    if (Date.now() - startedAt < REVEAL_FOR_MS) frame = requestAnimationFrame(attempt);
  };
  frame = requestAnimationFrame(attempt);
  return () => cancelAnimationFrame(frame);
}

/**
 * Shows a search's record once it's in the section's data (which may still be
 * loading when the request arrives): `show` opens its list / tab and selects
 * it, then its row is revealed. A record that never turns up (hidden, or not
 * this profile's) leaves the page as it is.
 */
export function useRevealSearchTarget(
  focus: SearchFocus | undefined,
  has: (id: string) => boolean,
  show: (id: string) => void,
): void {
  const [pending, setPending] = useState<string | null>(null);
  const showRef = useRef(show);
  showRef.current = show;
  useEffect(() => {
    if (focus) setPending(focus.id);
  }, [focus]);
  const found = pending !== null && has(pending);
  useEffect(() => {
    if (!found || pending === null) return;
    showRef.current(pending);
    setPending(null);
    // Not cancelled on the re-render that clearing `pending` causes; it stops by itself.
    revealSearchRow(pending);
  }, [found, pending]);
}
